
import { equipSoundFor, type SfxPlayer } from "../audio/sfx";
import { formatYang, money } from "../money";
import { WEAR_SLOTS, WearSlot, wornIn } from "../wardrobe";
import { elem, installChrome, makeDraggable } from "./chrome";
import { itemArtNode } from "./itemart";
import { isAttachableSpiritStoneVnum } from "./attachmetin";
import { iconCellsTall } from "./itemicon";
import {
  ItemTooltip,
  attachHoverTooltip,
  slotTooltipData,
  type ItemBonusView,
  type ItemTooltipData,
} from "./tooltip";

export interface BagSlotModel {
  readonly slot: number;
  /** Which item this is. Used to look its icon up and to span size>1 cells
   *  from that icon's packed height (`iconCellsTall`). Pricing, clicks and
   *  whether a move lands are still the server's. */
  readonly vnum: number;
  readonly count: number;
  readonly name: string;
  /** 1 weapon, 2 armour, 3 potion, 4 other. */
  readonly kind: number;
  /** What a dealer would pay for this whole slot. Zero means nobody will buy
   *  it. Already rounded server-side -- never multiplied up from a per-item
   *  price here, because that arithmetic belongs to the server and a client's
   *  guess is a quote it will refuse. */
  readonly sellValue: number;
  /** Server-reported first socket: 0 empty, 1 broken, otherwise fitted. */
  readonly socket?: number;
  /** Server-reported remaining per-instance sockets; never client-authored. */
  readonly socket1?: number;
  readonly socket2?: number;

  readonly levelLimit?: number;
  readonly attackMin?: number;
  readonly attackMax?: number;
  readonly defence?: number;
  readonly healHp?: number;
  readonly healHpPct?: number;
  readonly barredJobs?: number;
  readonly bonuses?: readonly ItemBonusView[];
}

function tooltipDataFor(slot: BagSlotModel): ItemTooltipData {
  return slotTooltipData(slot, { price: slot.sellValue });
}

export interface BagModel {
  readonly gold: number;
  readonly capacity: number;
  readonly slots: readonly BagSlotModel[];
  readonly worn: readonly BagSlotModel[];
}

function socketLabel(slot: BagSlotModel): string {
  const sockets = [slot.socket ?? 0, slot.socket1 ?? 0, slot.socket2 ?? 0];
  if (sockets.every((socket) => socket === 0)) return "";
  // 0 is a socket never opened; 1 and 2 are opened and empty (`item.cpp:1127-1137`);
  // `char_item.cpp:4851-4861` writes ITEM_BROKEN_METIN_VNUM (28960) on a failed fit.
  const labels = sockets.filter((socket) => socket !== 0).map((socket) => {
    if (socket === 1 || socket === 2) return "empty";
    if (socket === 28_960) return "broken";
    return "fitted";
  });
  return ` — sockets: ${labels.join(", ")}`;
}

export interface BagActions {
  onUse?: (slot: number) => void;
  /** Put a whole slot back on the ground. Fired only from the Yes button of
   *  the drop question -- see the module docstring. */
  onDrop?: (slot: number) => void;
  /** Released outside every panel: offer the slot to the character under that
   *  client point. True when there was one to offer it to, and then no drop
   *  question is asked (`game.py:1284-1295`). */
  onGiveAt?: (slot: number, clientX: number, clientY: number) => boolean;
  /** Put this amount of the purse on the ground. No balance or resulting
   *  purse crosses the request; the next `Bag` is the answer. */
  onDropGold?: (amount: number) => void;
  onUnequip?: (wear: number) => void;
  /** Sell mode only: sell the whole stack in a bag slot to the named dealer. */
  onSell?: (npcId: number, slot: number) => void;
  onMove?: (source: number, destination: number) => void;
  onUseOnItem?: (slot: number, target: number) => void;
  onArm?: (slot: number | null, name: string) => void;
  /** `DSSButton`, hung on the equipment board itself
   *  (`locale/en/ui/inventorywindow.py:96-110`), whose event is
   *  `self.interface.ToggleDragonSoulWindow()` (`uiinventory.py:474-476`). */
  onOpenDragonSoul?: () => void;
  onOpenMall?: () => void;
  /** `CostumeButton` (`locale/en/ui/inventorywindow.py:126-137`). Opens
   *  `CostumeWindow` for wear 19/20. */
  onCostume?: () => void;
  /** A drag ended on a Fast Access Field (`uitaskbar.py:829-847` AddQuickSlot).
   *  Bind, do not drop: the original's inventory drag onto a quick slot is not
   *  `__DropItem`. Occupancy of the bag grid is unchanged. */
  onBindQuickSlot?: (quickIndex: number, bagSlot: number) => void;
  /** A drag ended on the trade window's own grid: `SelectOwnerEmptySlot`
   *  offers the attached inventory cell (`uiexchange.py:118-144`). */
  onDropOnExchange?: (bagSlot: number) => void;
  /** A drag ended on a storeroom cell: `SelectEmptySlot` checks the attached
   *  inventory cell in there (`uisafebox.py:414-424`). */
  onDropOnSafebox?: (bagSlot: number, cell: number) => void;
  /** A drag ended on a stall builder cell: `OnSelectEmptySlot` takes the
   *  attached inventory cell for that cell (`uiprivateshopbuilder.py:220-232`). */
  onDropOnStallBuilder?: (bagSlot: number, cell: number) => void;
  /** Every pointer move of a drag in progress, so the builder grid can light
   *  up the cells the attached item would land on (`OnRenderPickingSlot`,
   *  `PythonGridSlotWindow.cpp:7-19`). `cell` is null once the pointer leaves
   *  the grid, or once the drag ends. */
  onHoverStallBuilder?: (vnum: number, cell: number | null) => void;
  /** The clip for equipping, unequipping or dropping a bag slot -- see
   *  `equipSoundFor`. Optional so a test fixture built without one stays
   *  silent; `main.ts` wires the shared `Sfx` here. */
  sfx?: SfxPlayer;
}

/** `TASKBAR_DRAGON_SOUL` (`locale_interface.txt:314`), `DSSButton`'s own
 *  `tooltip_text` (`locale/en/ui/inventorywindow.py:104`). */
export const DRAGON_SOUL_BUTTON_LABEL = "Dragon Stone Alchemy";
/** `MALL_TITLE` (`locale_interface.txt:189`), `MallButton`'s
 *  (`locale/en/ui/inventorywindow.py:119`). */
export const MALL_BUTTON_LABEL = "Item Shop Storeroom";
/** `COSTUME_TITLE` (`locale_interface.txt:17`), `CostumeButton`'s
 *  (`locale/en/ui/inventorywindow.py:132`). English pack spells "Costumes". */
export const COSTUME_BUTTON_LABEL = "Costumes";

export { WEAR_SLOTS };

/** Layout faces on Equipment_Base: 11 wire wear cells plus the ARROW hole
 *  at source index +9 (`inventorywindow.py:86`). The hole is not a WearSlot. */
export const EQUIPMENT_BOARD_CELLS = 12;

export const BAG_WINDOW_WIDTH = 176;
export const BAG_WINDOW_HEIGHT = 565;
export const BAG_PAGE_COLUMNS = 5;
export const BAG_PAGE_ROWS = 9;
export const BAG_PAGE_SIZE = BAG_PAGE_COLUMNS * BAG_PAGE_ROWS;
/** The legacy window always declares its two inventory tabs
 * (`inventorywindow.py:194-239`), even when a sparse frame has fewer cells. */
export const BAG_PAGE_COUNT = 2;
/** The source's two inventory radio buttons visibly read `I` and `II`, not
 * browser-style decimal page numbers (`inventorywindow.py:193-245`). */
export const BAG_PAGE_LABELS = ["I", "II"] as const;
export const BAG_PAGE_TOOLTIPS = ["1. Inventory", "2. Inventory"] as const;
/** Equipment base runs from y=33 to the inventory tabs at y=224 in the
 * legacy board (`inventorywindow.py:57-95,194-235`): 191px. */
export const EQUIPMENT_BASE_HEIGHT = 191;
export const GOLD_SLOT_WIDTH = 130;
export const GOLD_SLOT_HEIGHT = 18;
export const GOLD_SLOT_LEFT = 31;
export const GOLD_SLOT_TOP = 537;
export const GOLD_SLOT_SKIN = "/ui/parameter_slot_05.png";
/** `Money` is right-aligned with x=3 inside that source slot
 * (`inventorywindow.py:292-303`). */
export const GOLD_SLOT_TEXT_INSET = 3;
/** Standard legacy TitleBar skin height for the 161px title control
 * (`inventorywindow.py:35-49`). */
export const BAG_TITLE_BAR_HEIGHT = 20;
/** The legacy inventory TitleBar explicitly uses its yellow title variant
 * (`inventorywindow.py:35-49`). This is the imported UI's shared gold. */
export const BAG_TITLE_COLOR = "#e8c877";
/** The same TitleBar begins at x=8,y=7 in the fixed 176px inventory board
 * (`inventorywindow.py:35-49`). Keeping the y coordinate here makes the
 * browser's visible title sit on the legacy board furniture, rather than
 * merely sharing its dimensions. */
export const BAG_TITLE_BAR_TOP = 7;
export const BAG_CAPACITY_MAX = 90;

export const BELT_START = BAG_CAPACITY_MAX;

/** "Put it wherever there is room", the quick-slot index Ctrl-click asks for.
 *  `RequestAddToEmptyLocalQuickSlot` names no slot (`uiinventory.py:726`); the
 *  bar finds the first free one. Negative so it can never collide with a real
 *  index a drag produced. */
export const FIRST_EMPTY_QUICK_SLOT = -1;

export const BELT_CELL_COUNT = 16;

/** `WearSlot.BELT`, named rather than read off `WEAR_SLOTS`'s last entry so
 *  the belt strip's visibility does not quietly depend on that array's order. */
const BELT_WEAR = WearSlot.BELT;

/** Which item classes can be worn. Weapons and armour, which is the same rule
 *  `slot_for` applies -- restated here only to grey out a cell, never to decide:
 *  clicking a potion still asks, and the server still answers. */
export function looksWearable(kind: number): boolean {
  return kind === 1 || kind === 2 || kind === 6;
}

/** The class silhouettes live in `ui/itemart.ts` now, with the one rule for
 *  choosing between a painting and one of them. Re-exported here because this
 *  is where every panel has always imported them from. */
export { iconFor, pathFor } from "./itemart";

/** Slots drawn when the server has not said how many there are yet. Zero would
 *  be an empty box that looks broken during the half second before the first
 *  `Bag` frame lands. */
export const PLACEHOLDER_CAPACITY = 45;

/** A stack's badge. One is not written, because a lone item is not a stack. */
export function stackLabel(count: number): string {
  if (!Number.isFinite(count) || count <= 1) return "";
  if (count < 1000) return String(Math.floor(count));
  return `${Math.floor(count / 1000)}k`;
}


/** The bag before the server has said anything, and what sell mode resets to. */
const EMPTY_BAG: BagModel = { gold: 0, capacity: 0, slots: [], worn: [] };

export function dropQuestion(name: string, count: number): string {
  return count > 1 ? `Do you want to drop ${name} ${count}?` : `Do you want to drop ${name}?`;
}

export function sellQuestion(name: string, count: number, price: number): string {
  return count > 1
    ? `Do you want to sell ${name} ${count} for ${formatYang(price)}?`
    : `Do you want to sell ${name} for ${formatYang(price)}?`;
}

/** `SHOP_CANNOT_SELL_ITEM` (`locale_game.txt:432`) -- the shipped line
 *  `__SellItem` pops for an ANTIFLAG_SELL item (`uiinventory.py:791-797`).
 *  This panel used to invent "nobody is buying this" for the same state. */
export const CANNOT_SELL = "This Item cannot be sold.";

/** `YES` / `NO` (`locale_interface.txt:325,207`) -- the two buttons
 *  `QuestionDialog`'s own board carries (`uiscript/questiondialog.py:38-73`). */
export const YES = "Yes";
export const NO = "No";

const USE_ON_HINT = "Click what to use it on · click it again to cancel";
function useOnHint(name: string): string {
  return name
    ? `Using ${name} · click what to use it on · click it again to cancel`
    : `Using item · ${USE_ON_HINT}`;
}

const DRAG_SLOP_PX = 10;

/** One drag in progress: which slot it started from, whether it has moved far
 *  enough to count as a drag at all, and the floating icon following the
 *  pointer while it decides. */
interface DragState {
  readonly sourceIndex: number;
  readonly vnum: number;
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly ghost: HTMLElement;
  moved: boolean;
}

export class BagPanel {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly closeEl: HTMLButtonElement;
  private readonly goldEl: HTMLButtonElement;
  private readonly goldForm: HTMLFormElement;
  private readonly goldInput: HTMLInputElement;
  private readonly gear: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly pages: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly worn: HTMLButtonElement[] = [];
  private readonly beltEl: HTMLElement;
  private readonly beltGrid: HTMLElement;
  private readonly beltToggle: HTMLButtonElement;
  private readonly belt: HTMLButtonElement[] = [];
  private beltOpen = false;
  /** The `QuestionDialog` currently open, or null. Holds the accept event
   *  rather than the question: nothing is sent until Yes, which is the whole
   *  point of the dialog (`uicommon.py:256-260`). */
  private pendingAsk: (() => void) | null = null;
  private readonly askEl: HTMLElement;
  private readonly askText: HTMLElement;
  private readonly askYes: HTMLButtonElement;
  private fingerprint = "";
  private open = false;
  private page = 0;
  /** Item class per bag slot, so a click knows whether it means "wear". */
  private kinds = new Map<number, number>();
  /** What a dealer would pay per bag slot, so a click in sell mode knows
   *  whether it means anything. Kept separately from `kinds` rather than
   *  folded into one map of objects: the two are read on different clicks and
   *  a shared shape would make every read of one carry the other's fields. */
  private sellValues = new Map<number, number>();
  /** The last bag the server sent, so entering or leaving sell mode -- which
   *  changes what a cell shows without the bag itself changing -- has
   *  something to redraw against. */
  private lastBag: BagModel = EMPTY_BAG;
  /** Set while a sale is open. `slot` only ever means a bag slot here, never a
   *  worn one -- `SellItem` cannot name the gear strip, so it keeps its usual
   *  behaviour throughout a sale. */
  private sell: { readonly npcId: number; readonly dealerName: string } | null = null;
  /** One tooltip, shared by every slot -- at most one of forty-five-odd cells
   *  is ever under the pointer at a time. */
  private readonly tooltip: ItemTooltip;
  /** The drag in progress, or null between gestures. */
  private dragging: DragState | null = null;
  /** True for the one native `click` a real drag's own `pointerup` produces.
   *  Consumed by the click handler below, so a drag that ended over a slot
   *  never also fires that slot's ordinary use-or-wear action. */
  private justDragged = false;
  /** The slot a shift-click armed for `onUseOnItem`, or null between
   *  gestures. Cleared by the next click anywhere in the grid -- on the
   *  slot itself, which cancels; on any other, which fires -- and by the
   *  next `Bag` frame, which means the world moved on without waiting. */
  private armedUseSlot: number | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: BagActions = {},
  ) {
    installChrome(container.ownerDocument);
    this.tooltip = new ItemTooltip(container.ownerDocument);
    this.root = elem(document, "div", "bag m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    const header = elem(document, "div", "bag-header");

    this.titleEl = elem(document, "span", "bag-title m2-titlebar");
    this.titleEl.id = "bag-title";
    this.titleEl.style.top = `${BAG_TITLE_BAR_TOP}px`;
    this.titleEl.style.height = `${BAG_TITLE_BAR_HEIGHT}px`;
    this.titleEl.style.lineHeight = `${BAG_TITLE_BAR_HEIGHT}px`;
    this.titleEl.style.color = BAG_TITLE_COLOR;
    this.titleEl.textContent = "Inventory";
    this.root.setAttribute("aria-labelledby", this.titleEl.id);

    // `TitleBar.MakeTitleBar` always builds and shows a close button
    // (`ui.py:1551-1557`), and `InventoryWindow` wires it straight to
    // `Close` (`uiinventory.py:303`). Art comes from `m2-close`
    // (`chrome.ts`); the corner inset is sourced (`ui.py:1566-1574`).
    this.closeEl = document.createElement("button");
    this.closeEl.type = "button";
    this.closeEl.className = "bag-close m2-close";
    this.closeEl.title = "Close";
    this.closeEl.setAttribute("aria-label", "Close inventory");
    this.closeEl.addEventListener("click", () => this.toggle());
    makeDraggable(this.root, this.titleEl);

    this.goldEl = document.createElement("button");
    this.goldEl.type = "button";
    this.goldEl.className = "bag-gold";
    this.goldEl.style.left = `${GOLD_SLOT_LEFT}px`;
    this.goldEl.style.top = `${GOLD_SLOT_TOP}px`;
    this.goldEl.style.width = `${GOLD_SLOT_WIDTH}px`;
    this.goldEl.style.height = `${GOLD_SLOT_HEIGHT}px`;
    this.goldEl.style.backgroundImage = `url("${GOLD_SLOT_SKIN}")`;
    this.goldEl.style.backgroundRepeat = "no-repeat";
    this.goldEl.style.backgroundSize = `${GOLD_SLOT_WIDTH}px ${GOLD_SLOT_HEIGHT}px`;
    this.goldEl.style.textAlign = "right";
    this.goldEl.style.paddingRight = `${GOLD_SLOT_TEXT_INSET}px`;
    this.goldEl.textContent = formatYang(0);
    this.goldEl.title = "Drop gold";
    this.goldEl.addEventListener("click", () => this.openGoldDrop());

    header.append(this.titleEl, this.closeEl, this.goldEl);

    this.goldForm = elem(document, "form", "bag-gold-drop");
    this.goldForm.hidden = true;
    this.goldInput = elem(document, "input", "bag-gold-input");
    this.goldInput.type = "number";
    this.goldInput.inputMode = "numeric";
    this.goldInput.min = "1";
    this.goldInput.step = "1";
    this.goldInput.setAttribute("aria-label", "Gold to drop");
    const dropGold = document.createElement("button");
    dropGold.type = "submit";
    dropGold.textContent = "Drop";
    const cancelGold = document.createElement("button");
    cancelGold.type = "button";
    cancelGold.textContent = "Cancel";
    cancelGold.addEventListener("click", () => this.closeGoldDrop());
    this.goldForm.addEventListener("submit", (event) => {
      event.preventDefault();
      this.submitGoldDrop();
    });
    this.goldForm.append(this.goldInput, dropGold, cancelGold);

    this.askEl = elem(document, "div", "bag-ask");
    this.askEl.hidden = true;
    this.askEl.setAttribute("role", "dialog");
    this.askText = elem(document, "p", "bag-ask-text");
    this.askYes = document.createElement("button");
    this.askYes.type = "button";
    this.askYes.className = "bag-ask-yes";
    this.askYes.textContent = YES;
    this.askYes.addEventListener("click", () => this.acceptAsk());
    const askNo = document.createElement("button");
    askNo.type = "button";
    askNo.className = "bag-ask-no";
    askNo.textContent = NO;
    askNo.addEventListener("click", () => this.closeAsk());
    const askButtons = elem(document, "div", "bag-ask-buttons");
    askButtons.append(this.askYes, askNo);
    this.askEl.append(this.askText, askButtons);

    this.gear = elem(document, "div", "bag-gear");
    for (const { wear, label } of WEAR_SLOTS) {
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "bag-worn";
      cell.dataset.label = label;
      cell.addEventListener("click", () => {
        const worn = wornIn(this.lastBag.worn, wear);
        if (worn !== undefined) this.actions.sfx?.play(equipSoundFor(worn.kind, worn.vnum));
        this.actions.onUnequip?.(wear);
      });
      attachHoverTooltip(cell, this.tooltip, () => {
        const slot = wornIn(this.lastBag.worn, wear);
        return slot === undefined ? null : tooltipDataFor(slot);
      });
      this.worn.push(cell);
      this.gear.append(cell);
    }

    const arrowHole = elem(document, "div", "bag-arrow-hole");
    arrowHole.setAttribute("aria-hidden", "true");
    this.gear.append(arrowHole);
    this.gear.append(
      this.boardButton("bag-costume-button", COSTUME_BUTTON_LABEL, () => {
        this.actions.onCostume?.();
      }),
    );
    if (actions.onOpenDragonSoul !== undefined) {
      this.gear.append(
        this.boardButton("bag-dss-button", DRAGON_SOUL_BUTTON_LABEL, actions.onOpenDragonSoul),
      );
    }
    if (actions.onOpenMall !== undefined) {
      this.gear.append(
        this.boardButton("bag-mall-button", MALL_BUTTON_LABEL, actions.onOpenMall),
      );
    }

    this.grid = elem(document, "div", "bag-grid");

    // The window paints itself with the pack's own `bg.tga`
    // (`beltinventorywindow.py:17-18`), not with the nine-slice board chrome.
    this.beltEl = elem(document, "div", "bag-belt");
    this.beltEl.hidden = true;
    this.beltToggle = document.createElement("button");
    this.beltToggle.type = "button";
    this.beltToggle.className = "bag-belt-toggle";
    this.beltToggle.addEventListener("click", () => {
      this.beltOpen = !this.beltOpen;
      this.showBeltState();
    });
    this.beltGrid = elem(document, "div", "bag-belt-grid");
    for (let i = 0; i < BELT_CELL_COUNT; i++) {
      const cell = this.makeSlotButton("bag-belt-slot", () => BELT_START + i);
      this.belt.push(cell);
      this.beltGrid.append(cell);
    }
    this.beltEl.append(this.beltToggle, this.beltGrid);
    this.showBeltState();

    this.pages = elem(document, "div", "bag-pages");

    this.hintEl = elem(document, "div", "bag-hint", "");

    this.root.append(
      header, this.goldForm, this.gear, this.pages, this.grid, this.beltEl, this.hintEl,
      this.askEl,
    );
    container.append(this.root);

    container.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      // `InventoryWindow.Hide` answers a live QuestionDialog by closing *it*
      // and returning, rather than closing the window
      // (`uiinventory.py:434-437`, gated on
      // `constInfo.GET_ITEM_QUESTION_DIALOG_STATUS`).
      if (!this.askEl.hidden) {
        this.closeAsk();
        return;
      }
      if (!this.goldForm.hidden) {
        this.closeGoldDrop();
        return;
      }
      this.toggle();
    });

    this.resize(PLACEHOLDER_CAPACITY);
  }

  /** Grow or shrink one visible page; cells retain their server cell number. */
  private resize(capacity: number): void {
    const bounded = Math.max(0, Math.min(BAG_CAPACITY_MAX, Math.floor(capacity)));
    const pageCount = Math.max(1, Math.ceil(bounded / BAG_PAGE_SIZE));
    this.page = Math.min(this.page, pageCount - 1);
    // The legacy ItemSlot is always a full 5x9 visual grid
    // (`inventorywindow.py:250-263`). Cells beyond the server-reported
    // capacity remain disabled, but removing their hit surfaces makes a
    // partial frame visibly unlike the source window.
    const wanted = BAG_PAGE_SIZE;
    while (this.cells.length > wanted) {
      this.cells.pop()?.remove();
    }
    while (this.cells.length < wanted) {
      const index = this.cells.length;
      const cell = this.makeSlotButton("bag-slot", () => this.slotAt(index));
      this.cells.push(cell);
      this.grid.append(cell);
    }
    this.drawPages(Math.max(BAG_PAGE_COUNT, pageCount));
  }

  /** One of the equipment board's own buttons. Face art is the imported
   *  `.png` triple in `style.css`; tooltip and position are the script's. */
  private boardButton(
    cssClass: string, label: string, run: () => void,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `bag-board-button ${cssClass}`;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.addEventListener("click", () => run());
    return button;
  }

  private makeSlotButton(cssClass: string, resolveSlot: () => number): HTMLButtonElement {
    const cell = document.createElement("button");
    cell.type = "button";
    cell.className = `${cssClass} m2-slot`;
    cell.addEventListener("click", (event) => {
      if (!this.askEl.hidden) return;
      if (this.justDragged) {
        // The `pointerup` that ended a real drag already decided what it
        // meant; the `click` a browser fires right after it is an echo of
        // the same gesture, not a second one.
        this.justDragged = false;
        return;
      }
      if (this.armedUseSlot !== null) {
        const armed = this.armedUseSlot;
        this.setArmed(null);
        this.hintEl.textContent = "";
        this.repaint();
        const actual = resolveSlot();
        if (actual !== armed) this.actions.onUseOnItem?.(armed, actual);
        return;
      }
      if (this.sell !== null) {
        // Checked again here rather than trusted to `disabled`: a button
        // does not fire a click while disabled in a browser, but nothing
        // stops a caller from invoking the handler directly, and selling
        // nothing for the price of nothing is worse than a no-op.
        const actual = resolveSlot();
        const value = this.sellValues.get(actual) ?? 0;
        if (value <= 0) return;
        const held = this.lastBag.slots.find((s) => s.slot === actual);
        if (held === undefined) return;
        const npcId = this.sell.npcId;
        this.ask(sellQuestion(held.name, held.count, value), () => {
          this.actions.onSell?.(npcId, actual);
        });
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        const actual = resolveSlot();
        if (this.lastBag.slots.find((s) => s.slot === actual) === undefined) return;
        this.actions.onBindQuickSlot?.(FIRST_EMPTY_QUICK_SLOT, actual);
        return;
      }
      if (event.shiftKey) {
        // Arm this slot instead of using or wearing it. An empty cell has
        // nothing to arm -- `UseItemToItem` cannot name a slot with
        // nothing in it any more than `UseItem` can.
        const actual = resolveSlot();
        if (this.lastBag.slots.find((s) => s.slot === actual) === undefined) return;
        this.setArmed(actual);
        this.hintEl.textContent = useOnHint(
          this.lastBag.slots.find((s) => s.slot === actual)?.name ?? "",
        );
        this.repaint();
        return;
      }
      const actual = resolveSlot();
      this.playEquipSound(actual);
      this.actions.onUse?.(actual);
    });
    cell.addEventListener("contextmenu", (event) => {
      event.preventDefault();
      if (!this.askEl.hidden) return;
      if (this.armedUseSlot !== null) {
        // A right-click while armed reads as "never mind", not as "drop
        // the thing I was about to use" -- dropping was not the gesture
        // this player just made.
        this.setArmed(null);
        this.hintEl.textContent = "";
        this.repaint();
        return;
      }
      const actual = resolveSlot();
      // Legacy binds UseSlotEvent to UseItemSlot (`uiinventory.py:335-338`),
      // so right-click uses/equips the item too -- through the same one
      // message, for the same reason as the click above. Ground drops are drag
      // paths in the source, not a context-menu action.
      this.playEquipSound(actual);
      this.actions.onUse?.(actual);
    });
    cell.addEventListener("pointerdown", (event) => this.onSlotPointerDown(event, cell, resolveSlot()));
    cell.addEventListener("pointermove", (event) => this.onSlotPointerMove(event, cell));
    cell.addEventListener("pointerup", (event) => this.onSlotPointerUp(event, cell));
    cell.addEventListener("pointercancel", (event) => this.onSlotPointerUp(event, cell));
    attachHoverTooltip(cell, this.tooltip, () => {
      const slot = this.lastBag.slots.find((s) => s.slot === resolveSlot());
      return slot === undefined ? null : tooltipDataFor(slot);
    });
    return cell;
  }

  private slotAt(index: number): number {
    return this.page * BAG_PAGE_SIZE + index;
  }

  private drawPages(pageCount: number): void {
    this.pages.replaceChildren();
    this.pages.hidden = pageCount < 2;
    for (let page = 0; page < pageCount; page++) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "bag-page";
      button.textContent = BAG_PAGE_LABELS[page] ?? String(page + 1);
      const tip = BAG_PAGE_TOOLTIPS[page] ?? `${page + 1}. Inventory`;
      button.title = tip;
      button.setAttribute("aria-label", tip);
      button.disabled = page === this.page;
      button.addEventListener("click", () => {
        this.page = page;
        this.repaint();
      });
      this.pages.append(button);
    }
  }

  // -------------------------------------------------------------- dragging

  private onSlotPointerDown(event: PointerEvent, cell: HTMLButtonElement, index: number): void {
    // Left button or a finger only -- a right click is `contextmenu`'s to
    // handle, and starting a drag under it as well would make right-clicking
    // a slot pick it up a frame before the drop-it menu fires.
    if (event.button !== 0) return;
    // Same freeze as the click handler above: a question is open, the grid is
    // not (`uiinventory.py:639-641`).
    if (!this.askEl.hidden) return;
    // Selling has its own one-gesture-one-meaning rule already; a slot mid-sale
    // is not a slot anyone is rearranging.
    if (this.sell !== null) return;
    if (this.dragging !== null) return;
    if (cell.disabled) return;

    const slot = this.lastBag.slots.find((s) => s.slot === index);
    if (slot === undefined) return;

    event.preventDefault();
    cell.setPointerCapture(event.pointerId);
    this.tooltip.hide();
    this.dragging = {
      sourceIndex: index,
      vnum: slot.vnum,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      ghost: this.makeGhost(slot),
      moved: false,
    };
  }

  private onSlotPointerMove(event: PointerEvent, cell: HTMLButtonElement): void {
    const drag = this.dragging;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    if (!drag.moved) {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (Math.hypot(dx, dy) <= DRAG_SLOP_PX) return;
      drag.moved = true;
      cell.classList.add("bag-slot-dragging");
    }
    drag.ghost.style.left = `${event.clientX}px`;
    drag.ghost.style.top = `${event.clientY}px`;
    if (this.actions.onHoverStallBuilder !== undefined) {
      const under = this.root.ownerDocument.elementFromPoint(event.clientX, event.clientY);
      const destBuilder = under instanceof Element ? under.closest<HTMLElement>("[data-stall-builder-cell]") : null;
      this.actions.onHoverStallBuilder(
        drag.vnum,
        destBuilder === null ? null : Number(destBuilder.dataset.stallBuilderCell),
      );
    }
  }

  private onSlotPointerUp(event: PointerEvent, cell: HTMLButtonElement): void {
    const drag = this.dragging;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    this.dragging = null;
    cell.classList.remove("bag-slot-dragging");
    drag.ghost.remove();
    this.actions.onHoverStallBuilder?.(drag.vnum, null);

    if (!drag.moved) return; // A tap. The native `click` that follows is the answer.

    // The `click` a real drag's own release still produces is not this
    // gesture's second act -- see the grid's own click handler.
    this.justDragged = true;

    const doc = this.root.ownerDocument;
    if (event.type === "pointercancel") return; // Taken away mid-drag: goes home.
    const under = doc.elementFromPoint(event.clientX, event.clientY);
    const destCell = under instanceof Element ? under.closest<HTMLButtonElement>(".bag-slot") : null;
    const destBelt = under instanceof Element ? under.closest<HTMLButtonElement>(".bag-belt-slot") : null;
    const destWorn = under instanceof Element ? under.closest<HTMLButtonElement>(".bag-worn") : null;
    if (destWorn !== null) {
      if (looksWearable(this.kindAt(drag.sourceIndex))) {
        this.playEquipSound(drag.sourceIndex);
        this.actions.onUse?.(drag.sourceIndex);
      }
      return;
    }
    // The belt strip's sixteen cells are fixed absolute indices -- no paging
    // to resolve through, unlike the grid's `slotAt`.
    let destIndex = -1;
    if (destCell !== null) {
      const physical = this.cells.indexOf(destCell);
      if (physical >= 0) destIndex = this.slotAt(physical);
    } else if (destBelt !== null) {
      const physical = this.belt.indexOf(destBelt);
      if (physical >= 0) destIndex = BELT_START + physical;
    }
    if (destIndex >= 0) {
      if (destIndex === drag.sourceIndex) return;
      // A Spirit Stone dropped on an item is `AttachMetinToItem`, whose
      // question dialog comes before anything is sent (`uiinventory.py:748-749`).
      const onto = this.lastBag.slots.some((s) => s.slot === destIndex);
      if (onto && isAttachableSpiritStoneVnum(drag.vnum)) {
        this.actions.onUseOnItem?.(drag.sourceIndex, destIndex);
        return;
      }
      this.actions.onMove?.(drag.sourceIndex, destIndex);
      return;
    }
    const destExchange =
      under instanceof Element ? under.closest<HTMLElement>("[data-exchange-owner-cell]") : null;
    if (destExchange !== null && this.actions.onDropOnExchange !== undefined) {
      this.actions.onDropOnExchange(drag.sourceIndex);
      return;
    }
    const destSafebox =
      under instanceof Element ? under.closest<HTMLElement>("[data-safebox-cell]") : null;
    if (destSafebox !== null && this.actions.onDropOnSafebox !== undefined) {
      this.actions.onDropOnSafebox(drag.sourceIndex, Number(destSafebox.dataset.safeboxCell));
      return;
    }
    const destBuilder =
      under instanceof Element ? under.closest<HTMLElement>("[data-stall-builder-cell]") : null;
    if (destBuilder !== null && this.actions.onDropOnStallBuilder !== undefined) {
      this.actions.onDropOnStallBuilder(drag.sourceIndex, Number(destBuilder.dataset.stallBuilderCell));
      return;
    }
    // Released over a Fast Access Field. `AddQuickSlot` (`uitaskbar.py:829-847`)
    // binds the attached inventory cell; it is not a ground drop.
    const destQuick =
      under instanceof Element ? under.closest<HTMLElement>("[data-quick-index]") : null;
    if (destQuick !== null && this.actions.onBindQuickSlot !== undefined) {
      const quickIndex = Number(destQuick.dataset.quickIndex);
      if (Number.isInteger(quickIndex) && quickIndex >= 0) {
        this.actions.onBindQuickSlot(quickIndex, drag.sourceIndex);
        return;
      }
    }
    // Released over this panel's own furniture -- the header, the gear strip,
    // the hint, the page tabs. Not a gesture at all, and it still sends
    // nothing: the source slot was never touched, so it is already back where
    // it started.
    if (under instanceof Element && this.root.contains(under)) return;
    const held = this.lastBag.slots.find((s) => s.slot === drag.sourceIndex);
    if (held === undefined) return;
    const source = drag.sourceIndex;
    // `__PutItem` (`game.py:1289-1290`): over an NPC the item is given, not dropped.
    if (this.actions.onGiveAt?.(source, event.clientX, event.clientY) === true) return;
    this.ask(dropQuestion(held.name, held.count), () => {
      // `DROPSOUND_*` (`playersettingmodule.py:329-333`): the same per-kind
      // clip `equipSoundFor` already picks for equip/unequip, since every
      // `DROPSOUND_*` equals its `USESOUND_*` sibling except DEFAULT.
      this.actions.sfx?.play(equipSoundFor(held.kind, held.vnum));
      this.actions.onDrop?.(source);
    });
  }

  /** Open the QuestionDialog. Nothing is sent until Yes -- `SetAcceptEvent`
   *  is the only path to a packet in every one of the source's own callers
   *  (`uicommon.py:256-260`, `uiinventory.py:811`, `game.py:1350`). */
  private ask(text: string, accept: () => void): void {
    this.askText.textContent = text;
    this.pendingAsk = accept;
    this.askEl.hidden = false;
    this.askYes.focus();
  }

  private acceptAsk(): void {
    const accept = this.pendingAsk;
    this.closeAsk();
    accept?.();
  }

  /** Cancel, or Escape. `SetCancelEvent` runs `OnCloseQuestionDialog`, which
   *  closes and sends nothing (`uiinventory.py:630-636`). */
  private closeAsk(): void {
    this.pendingAsk = null;
    this.askEl.hidden = true;
  }

  /** The question currently on screen, or "" when there is none. */
  get asking(): string {
    return this.askEl.hidden ? "" : (this.askText.textContent ?? "");
  }

  /** Expand or collapse the belt strip, and say which the button does next.
   *  `OpenInventory` shows the layer and hides `ExpandBtn`; `CloseInventory`
   *  does the reverse (`uiinventory.py:145-157`). */
  private showBeltState(): void {
    this.beltEl.classList.toggle("bag-belt-closed", !this.beltOpen);
    this.beltGrid.hidden = !this.beltOpen;
    this.beltToggle.textContent = this.beltOpen ? "‹" : "›";
    const label = this.beltOpen ? "Close the belt" : "Open the belt";
    this.beltToggle.title = label;
    this.beltToggle.setAttribute("aria-label", label);
    this.beltToggle.setAttribute("aria-expanded", String(this.beltOpen));
  }

  /** A small floating copy of a slot's own art, following the pointer while a
   *  drag decides whether it is one. */
  private makeGhost(slot: BagSlotModel): HTMLElement {
    const doc = this.root.ownerDocument;
    const ghost = elem(doc, "div", "bag-drag-ghost");
    ghost.append(this.artFor(slot));
    doc.body.append(ghost);
    return ghost;
  }

  update(bag: BagModel): void {
    this.lastBag = bag;
    if (this.armedUseSlot !== null) {
      // The world moved on while a cell was armed -- a chest opened from
      // another device, an item picked up, anything. The arm was a promise
      // about a bag that no longer exists, so it lapses rather than firing
      // against whatever is in that cell now.
      this.setArmed(null);
      this.hintEl.textContent = "";
      this.repaint();
      return;
    }
    const print = this.fingerprintFor(bag);
    if (print === this.fingerprint) return;
    this.fingerprint = print;
    // A question named a slot in the bag as it was; the world has moved on.
    // It lapses rather than firing against whatever is in that cell now --
    // the same rule the armed slot above follows, for the same reason.
    this.closeAsk();
    this.paint(bag);
  }

  /** One string that changes exactly when the drawn grid must. Sell mode is
   *  folded in here as well as handled by `repaint`'s bypass: a `Bag` frame
   *  that lands *while* selling still has to repaint against the new prices. */
  private fingerprintFor(bag: BagModel): string {
    const capacity = bag.capacity > 0 ? bag.capacity : PLACEHOLDER_CAPACITY;
    const describe = (slots: readonly BagSlotModel[]) =>
      slots
        .map((s) => {
          const bonuses = (s.bonuses ?? []).map((b) => `${b.apply}:${b.value}`).join("/");
          return [
            s.slot, s.vnum, s.kind, s.count, s.name, s.sellValue,
            s.levelLimit, s.attackMin, s.attackMax, s.defence, s.healHp,
            s.healHpPct, s.barredJobs, s.socket ?? 0, s.socket1 ?? 0,
            s.socket2 ?? 0, bonuses,
          ].join(":");
        })
        .join(",");
    const mode = this.sell === null ? "" : `sell:${this.sell.npcId}`;
    return `${mode}|${bag.gold}|${capacity}|${describe(bag.slots)}|${describe(bag.worn)}`;
  }

  private paint(bag: BagModel): void {
    const capacity = bag.capacity > 0 ? bag.capacity : PLACEHOLDER_CAPACITY;
    this.resize(capacity);
    this.goldEl.textContent = formatYang(bag.gold);
    this.goldEl.disabled = bag.gold <= 0;
    this.goldInput.max = String(Math.max(0, Math.floor(bag.gold)));
    if (bag.gold <= 0) this.goldForm.hidden = true;
    this.kinds = new Map(bag.slots.map((slot) => [slot.slot, slot.kind]));
    this.sellValues = new Map(bag.slots.map((slot) => [slot.slot, slot.sellValue]));

    // The gear strip does not change in sell mode: `SellItem` names a bag
    // slot and nothing else, so a worn piece is never for sale and keeps its
    // ordinary "click to take off".
    const wearing = new Map(bag.worn.map((slot) => [slot.slot, slot]));
    for (let index = 0; index < this.worn.length; index++) {
      const cell = this.worn[index];
      const wear = WEAR_SLOTS[index].wear;
      const slot = wearing.get(wear);
      if (slot === undefined) {
        cell.replaceChildren();
        cell.title = WEAR_SLOTS[index].label;
        cell.setAttribute("aria-label", WEAR_SLOTS[index].label);
        cell.className = "bag-worn";
        cell.disabled = true;
        continue;
      }
      cell.disabled = false;
      cell.className = `bag-worn bag-slot-${slot.kind}`;
      cell.title = `${slot.name}${socketLabel(slot)} — click to take off`;
      cell.setAttribute("aria-label", cell.title);
      cell.replaceChildren(this.artFor(slot));
    }

    const held = new Map(bag.slots.map((slot) => [slot.slot, slot]));
    const pageOrigin = this.page * BAG_PAGE_SIZE;
    const covered = new Set<number>();
    const spanOf = new Map<number, number>();
    for (const slot of bag.slots) {
      const local = slot.slot - pageOrigin;
      if (local < 0 || local >= this.cells.length) continue;
      const tall = iconCellsTall(slot.vnum);
      spanOf.set(local, tall);
      for (let step = 1; step < tall; step++) {
        const next = local + step * BAG_PAGE_COLUMNS;
        if (next < this.cells.length) covered.add(next);
      }
    }
    for (let index = 0; index < this.cells.length; index++) {
      const absolute = this.slotAt(index);
      const isCovered = covered.has(index) && !held.has(absolute);
      this.paintCell(this.cells[index], held.get(absolute), absolute, "bag-slot");
      this.placeOnGrid(
        this.cells[index],
        index,
        BAG_PAGE_COLUMNS,
        isCovered ? 1 : (spanOf.get(index) ?? 1),
        isCovered,
      );
    }

    this.beltEl.hidden = !wearing.has(BELT_WEAR);
    const beltCovered = new Set<number>();
    const beltSpan = new Map<number, number>();
    for (const slot of bag.slots) {
      if (slot.slot < BELT_START || slot.slot >= BELT_START + this.belt.length) continue;
      const local = slot.slot - BELT_START;
      const tall = iconCellsTall(slot.vnum);
      beltSpan.set(local, tall);
      for (let step = 1; step < tall; step++) {
        const next = local + step * 4;
        if (next < this.belt.length) beltCovered.add(next);
      }
    }
    for (let i = 0; i < this.belt.length; i++) {
      const absolute = BELT_START + i;
      const isCovered = beltCovered.has(i) && !held.has(absolute);
      this.paintCell(this.belt[i], held.get(absolute), absolute, "bag-belt-slot");
      this.placeOnGrid(
        this.belt[i],
        i,
        4,
        isCovered ? 1 : (beltSpan.get(i) ?? 1),
        isCovered,
      );
    }
  }

  private placeOnGrid(
    cell: HTMLButtonElement,
    index: number,
    columns: number,
    span: number,
    covered: boolean,
  ): void {
    const col = (index % columns) + 1;
    const row = Math.floor(index / columns) + 1;
    const rows = columns === BAG_PAGE_COLUMNS ? BAG_PAGE_ROWS : 4;
    const tall = Math.min(Math.max(1, span), rows - row + 1);
    cell.style.gridColumn = String(col);
    cell.style.gridRow = tall > 1 ? `${row} / span ${tall}` : String(row);
    cell.style.height = tall > 1 ? `${tall * 32}px` : "";
    cell.style.visibility = covered ? "hidden" : "";
  }

  private paintSellCell(cell: HTMLButtonElement, slot: BagSlotModel, baseClass = "bag-slot"): void {
    const sellable = slot.sellValue > 0;
    cell.disabled = !sellable;
    cell.className = `${baseClass} m2-slot bag-slot-${slot.kind}${sellable ? "" : " bag-slot-unsellable"}`;
    cell.title = sellable
      ? `${slot.name} — sells for ${money(slot.sellValue)}`
      // `SHOP_CANNOT_SELL_ITEM`, the shipped line for this exact state
      // (`uiinventory.py:791-797`), rather than a sentence this file made up.
      : `${slot.name} — ${CANNOT_SELL}`;
    cell.setAttribute("aria-label", cell.title);

    const glyph = this.artFor(slot);
    const badge = elem(document, "span", "bag-count bag-count-sell", money(slot.sellValue));
    cell.replaceChildren(glyph, badge);
  }

  private paintCell(
    cell: HTMLButtonElement, slot: BagSlotModel | undefined, absoluteIndex: number, baseClass: string,
  ): void {
    if (slot === undefined) {
      cell.textContent = "";
      cell.title = "";
      cell.removeAttribute("aria-label");
      cell.className = `${baseClass} m2-slot`;
      cell.disabled = true;
      return;
    }
    if (this.sell !== null) {
      this.paintSellCell(cell, slot, baseClass);
      return;
    }
    cell.disabled = false;
    cell.className =
      absoluteIndex === this.armedUseSlot
        ? `${baseClass} m2-slot bag-slot-${slot.kind} bag-slot-armed`
        : `${baseClass} m2-slot bag-slot-${slot.kind}`;
    cell.title = `${slot.count > 1 ? `${slot.name} x${slot.count}` : slot.name}${socketLabel(slot)}`;
    cell.setAttribute("aria-label", cell.title);

    // The name never becomes markup: it goes in `title` as text and nowhere
    // else. The same rule the chat box follows, for the same reason -- an item
    // name is server data, and server data is never markup.
    const glyph = this.artFor(slot);

    const badge = elem(document, "span", "bag-count", stackLabel(slot.count));

    cell.replaceChildren(glyph, badge);
  }

  /** The original's painting for this item, or its class glyph when there is
   *  none. Every cell in the panel -- held, worn, and for sale -- goes through
   *  here, so one item never looks like two different things in two places. */
  private artFor(slot: BagSlotModel): Element {
    return itemArtNode(slot.vnum, slot.kind, this.root.ownerDocument);
  }

  /** Open the amount prompt from the purse itself, as the original inventory
   *  does at `uiinventory.py:489-511`. It caps the edit to the last server
   *  purse shown for usability only; the server repeats the real check in
   *  `CHARACTER::DropGold` (`char_item.cpp:5234-5237`). */
  private openGoldDrop(): void {
    if (this.lastBag.gold <= 0 || this.sell !== null) return;
    this.goldInput.value = "";
    this.goldInput.max = String(Math.floor(this.lastBag.gold));
    this.goldForm.hidden = false;
    this.goldInput.focus();
  }

  /** Close is deliberately separate from accept: Escape and Cancel must never
   *  send an amount (`uipickmoney.py:35-39,91-108`). */
  private closeGoldDrop(): void {
    this.goldInput.blur();
    this.goldForm.hidden = true;
  }

  private submitGoldDrop(): void {
    const requested = Number(this.goldInput.value);
    if (!Number.isFinite(requested) || requested <= 0) return;
    // The 2014 dialog applies `min(money, maxValue)` before its callback
    // (`uipickmoney.py:99-106`). This is the same courtesy, never authority:
    // the message still carries only an amount and the server owns the purse.
    const amount = Math.min(Math.floor(requested), Math.floor(this.lastBag.gold));
    if (amount <= 0) return;
    this.actions.onDropGold?.(amount);
    this.closeGoldDrop();
  }

  private kindAt(slot: number): number {
    return this.kinds.get(slot) ?? 0;
  }

  private playEquipSound(index: number): void {
    const slot = this.lastBag.slots.find((s) => s.slot === index);
    if (slot === undefined || !looksWearable(slot.kind)) return;
    this.actions.sfx?.play(equipSoundFor(slot.kind, slot.vnum));
  }

  private setArmed(slot: number | null): void {
    if (slot === this.armedUseSlot) return;
    this.armedUseSlot = slot;
    this.actions.onArm?.(
      slot,
      slot === null
        ? ""
        : this.lastBag.slots.find((held) => held.slot === slot)?.name ?? "",
    );
  }

  disarm(): void {
    if (this.armedUseSlot === null) return;
    this.setArmed(null);
    this.hintEl.textContent = "";
    this.repaint();
  }

  /** `dealerName` is kept on `this.sell` for callers (`sellingTo`, the sell
   *  question) but never reaches the header or a hint line: `uiinventory.py`
   *  renames nothing and posts no text of its own for sell mode, which is a
   *  cursor state (`app.SELL == app.GetCursor()`, `:699,1119`), not a title. */
  enterSellMode(npcId: number, dealerName: string): void {
    this.sell = { npcId, dealerName };
    this.setArmed(null);
    this.root.classList.add("bag-selling");
    this.repaint();
  }

  /** Back to the ordinary bag. Restores what a click on the grid means --
   *  exactly -- because a sell mode that left a trace behind would make the
   *  next ordinary click do the wrong thing. */
  exitSellMode(): void {
    if (this.sell === null) return;
    this.sell = null;
    this.root.classList.remove("bag-selling");
    this.repaint();
  }

  /** The dealer currently being sold to, or null in the ordinary bag. */
  get sellingTo(): number | null {
    return this.sell?.npcId ?? null;
  }

  private repaint(): void {
    this.fingerprint = this.fingerprintFor(this.lastBag);
    this.paint(this.lastBag);
  }

  toggle(): boolean {
    this.open = !this.open;
    this.root.hidden = !this.open;
    // A tooltip left over from the pointer that was on a slot the instant
    // this closed would otherwise float in front of whatever opens next.
    if (!this.open) {
      this.tooltip.hide();
      this.goldForm.hidden = true;
      this.closeAsk();
    }
    return this.open;
  }

  get showing(): boolean {
    return this.open;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
